import React, { useCallback, useEffect, useState } from "react";
import {
  Link2, RefreshCw, Loader2, Play, ExternalLink, AlertTriangle,
  CheckCircle2, XCircle, Eye, FileWarning, ArrowUpRight, Pencil, X, ShieldAlert,
} from "lucide-react";
import { toast } from "sonner";
import {
  adminSourcesAudit, adminSourcesHealth, adminSourceRecheck, adminSourceMarkReview,
  adminSourceDependencies, adminSourceEdit,
} from "../../lib/api";

const STATUS_META = {
  healthy:      { label: "HEALTHY",      color: "#5DD4B3" },
  redirect:     { label: "REDIRECTED",   color: "#CBB892" },
  uncertain:    { label: "UNCERTAIN",    color: "#E0A526" },
  needs_review: { label: "NEEDS REVIEW", color: "#9FA8D4" },
  mismatch:     { label: "MISMATCH",     color: "#D92D20" },
  dead:         { label: "DEAD",         color: "#D92D20" },
};
const STATUS_TABS = ["all", "dead", "redirect", "uncertain", "needs_review", "mismatch", "healthy"];

const Pill = ({ status, large = false }) => {
  const m = STATUS_META[status] || { label: status?.toUpperCase() || "—", color: "#A39E93" };
  return (
    <span
      data-testid={`source-status-${status}`}
      style={{ color: m.color, borderColor: m.color }}
      className={`inline-flex items-center gap-1 border font-mono uppercase tracking-[0.25em] ${
        large ? "text-[10px] px-2.5 py-1" : "text-[9px] px-2 py-0.5"
      }`}
    >
      {m.label}
    </span>
  );
};

export const AdminSourcesPanel = ({ token, userToken = null }) => {
  const [tab, setTab] = useState("all");
  const [q, setQ] = useState("");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({});
  const [lastAudit, setLastAudit] = useState(null);
  const [auditing, setAuditing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [editingRow, setEditingRow] = useState(null);

  const load = useCallback(async () => {
    if (!token && !userToken) return;
    setLoading(true);
    try {
      const opts = { limit: 300 };
      if (tab !== "all") opts.status = tab;
      if (q.trim()) opts.q = q.trim();
      const r = await adminSourcesHealth(token, opts, userToken);
      setRows(r.rows || []); setCounts(r.counts || {});
      setLastAudit(r.last_audit || null);
      setTotal(r.total || 0);
    } catch (e) { toast.error("Failed to load source health"); }
    finally { setLoading(false); }
  }, [token, userToken, tab, q]);
  useEffect(() => { load(); }, [load]);

  // Poll while audit running
  useEffect(() => {
    if (lastAudit?.status !== "running") return;
    const t = setInterval(load, 6000);
    return () => clearInterval(t);
  }, [lastAudit, load]);

  const runAudit = async () => {
    if (!window.confirm("Start a full source health audit? This will HTTP-check every source URL across all dossiers.")) return;
    setAuditing(true);
    try {
      await adminSourcesAudit(token, {}, userToken);
      toast.success("Source audit started — results will populate as URLs are checked.");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Audit failed to start"); }
    finally { setAuditing(false); }
  };

  const recheck = async (r) => {
    const hid = `${r.topic_id}:${r.source_idx}`;
    try {
      await adminSourceRecheck(token, hid, userToken);
      toast.success("Rechecked");
      load();
    } catch { toast.error("Recheck failed"); }
  };

  const mark = async (r, status) => {
    const hid = `${r.topic_id}:${r.source_idx}`;
    const note = window.prompt(`Optional note for "${status}":`, "") || null;
    try {
      await adminSourceMarkReview(token, hid, status, note, userToken);
      toast.success(`Marked as ${status.replace(/_/g, " ")}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Mark failed"); }
  };

  const total_all = Object.values(counts).reduce((a, b) => a + b, 0);

  return (
    <div data-testid="admin-sources-view">
      <div className="flex flex-wrap items-end justify-between gap-3 mb-3">
        <div>
          <div className="font-mono text-[10px] tracking-[0.3em] text-[#D92D20] mb-2 flex items-center gap-2">
            <Link2 size={13} /> // SOURCE INTEGRITY
          </div>
          <h1 className="font-display text-3xl sm:text-4xl text-[#E4DFD3]">Source health</h1>
          <p className="text-[#A39E93] mt-1 text-sm max-w-2xl">
            Dead-link &amp; redirect audit across all published dossier sources. Run a sweep,
            triage anything dead or drifted, mark items for manual review, and keep the
            citation layer honest.
          </p>
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          <button onClick={runAudit} disabled={auditing || lastAudit?.status === "running"} data-testid="sources-run-audit"
            className="flex-1 sm:flex-initial flex items-center justify-center gap-2 bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-4 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
            {auditing || lastAudit?.status === "running"
              ? <><Loader2 className="animate-spin" size={12} /> Auditing</>
              : <><Play size={12} /> Run full audit</>}
          </button>
          <button onClick={load} data-testid="sources-refresh"
            className="flex items-center gap-2 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em]">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>

      {lastAudit && (
        <div className="border border-[#2D2A26] bg-[#0F0F0F] px-3 py-2 mb-3 font-mono text-[10px] text-[#757065] flex flex-wrap gap-4" data-testid="sources-last-audit">
          <span>last audit: <span className="text-[#A39E93]">{lastAudit.status}</span></span>
          {lastAudit.checked !== undefined && <span>checked: <span className="text-[#A39E93]">{lastAudit.checked}</span></span>}
          {lastAudit.started_at && <span>started: {new Date(lastAudit.started_at).toLocaleString()}</span>}
          {lastAudit.completed_at && <span>finished: {new Date(lastAudit.completed_at).toLocaleString()}</span>}
        </div>
      )}

      {/* Status tile summary */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-2 mb-4">
        {["healthy", "redirect", "uncertain", "needs_review", "mismatch", "dead"].map((k) => {
          const m = STATUS_META[k];
          return (
            <div key={k} className="border border-[#2D2A26] bg-[#0F0F0F] px-3 py-2" data-testid={`sources-count-${k}`}>
              <div className="font-mono text-[8px] uppercase tracking-[0.25em]" style={{ color: m.color }}>
                {m.label}
              </div>
              <div className="font-display text-xl text-[#E4DFD3]">{counts[k] || 0}</div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-3">
        <div className="flex overflow-x-auto whitespace-nowrap gap-1 -mx-1 px-1 flex-1 min-w-0 border-b border-[#2D2A26]">
          {STATUS_TABS.map((t) => {
            const c = t === "all" ? total_all : counts[t] || 0;
            const active = tab === t;
            return (
              <button key={t} onClick={() => setTab(t)} data-testid={`sources-tab-${t}`}
                className={"px-3 py-2 -mb-px font-mono text-[10px] uppercase tracking-[0.25em] border-b-2 shrink-0 " +
                  (active ? "border-[#D92D20] text-[#D92D20]" : "border-transparent text-[#A39E93] hover:text-[#E4DFD3]")}>
                {t.replace(/_/g, " ")} {c ? <span className="ml-1 text-[#757065]">·&nbsp;{c}</span> : null}
              </button>
            );
          })}
        </div>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="search URL…"
          data-testid="sources-search-input"
          className="bg-[#0F0F0F] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-3 py-2 font-mono text-xs text-[#E4DFD3] placeholder:text-[#757065] w-full sm:w-56"
        />
      </div>

      {rows.length === 0 ? (
        <div className="border border-dashed border-[#2D2A26] p-12 text-center font-mono text-xs uppercase tracking-widest text-[#757065]" data-testid="sources-empty">
          {lastAudit ? "Nothing in this view." : "No audit yet — run one to populate."}
        </div>
      ) : (
        <div className="border border-[#2D2A26] bg-[#131313]" data-testid="sources-list">
          <div className="px-3 py-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[#757065] border-b border-[#2D2A26]">
            Showing {rows.length} of {total}
          </div>
          {rows.map((r) => {
            const hid = `${r.topic_id}:${r.source_idx}`;
            return (
              <div key={hid} className="px-3 py-3 border-b border-[#2D2A26]" data-testid={`source-row-${hid}`}>
                <div className="flex flex-wrap items-center gap-2 mb-1">
                  <Pill status={r.status} />
                  {r.http_status && <span className="font-mono text-[9px] text-[#757065]">HTTP {r.http_status}</span>}
                  {r.redirect && <span className="font-mono text-[9px] text-[#CBB892]">→ redirect</span>}
                  {r.error && <span className="inline-flex items-center gap-1 font-mono text-[9px] text-[#D92D20]"><AlertTriangle size={9} /> {r.error}</span>}
                  <span className="ml-auto font-mono text-[9px] text-[#757065]">
                    {r.checked_at ? new Date(r.checked_at).toLocaleString() : ""}
                  </span>
                </div>
                <div className="text-sm text-[#E4DFD3] mb-1 break-words">
                  {r.title || "(untitled source)"}
                </div>
                <div className="flex flex-wrap gap-2 font-mono text-[10px] text-[#A39E93] mb-2 break-all">
                  <a href={r.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 hover:text-[#D92D20]">
                    {r.url} <ExternalLink size={9} />
                  </a>
                </div>
                {r.final_url && r.final_url !== r.url && (
                  <div className="font-mono text-[10px] text-[#CBB892] mb-1 break-all flex items-start gap-1">
                    <ArrowUpRight size={10} className="mt-0.5 shrink-0" />
                    final: {r.final_url}
                  </div>
                )}
                <div className="font-mono text-[10px] text-[#757065] mb-2">
                  dossier:{" "}
                  {r.topic_slug ? (
                    <a href={`/topic/${r.topic_slug}`} target="_blank" rel="noopener noreferrer" className="text-[#A39E93] hover:text-[#D92D20]">
                      {r.topic_title || r.topic_slug}
                    </a>
                  ) : (r.topic_title || r.topic_id)}
                  {" · "}publisher: <span className="text-[#A39E93]">{r.publisher || "—"}</span>
                </div>
                {r.manual_note && (
                  <div className="text-xs italic text-[#9FA8D4] mb-2">note: {r.manual_note}</div>
                )}
                <div className="flex flex-wrap gap-2">
                  <button onClick={() => setEditingRow(r)} data-testid={`source-edit-${hid}`}
                    className="inline-flex items-center gap-1 border border-[#E0A526]/50 hover:bg-[#1a1408] text-[#E0A526] px-2.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.25em]">
                    <Pencil size={10} /> Edit
                  </button>
                  <button onClick={() => recheck(r)} data-testid={`source-recheck-${hid}`}
                    className="inline-flex items-center gap-1 border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-2.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.25em]">
                    <RefreshCw size={10} /> Recheck
                  </button>
                  <button onClick={() => mark(r, "needs_review")} data-testid={`source-mark-review-${hid}`}
                    className="inline-flex items-center gap-1 border border-[#9FA8D4]/40 hover:bg-[#0c0e1a] text-[#9FA8D4] px-2.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.25em]">
                    <Eye size={10} /> Needs review
                  </button>
                  <button onClick={() => mark(r, "mismatch")} data-testid={`source-mark-mismatch-${hid}`}
                    className="inline-flex items-center gap-1 border border-[#D92D20]/40 hover:bg-[#1a0808] text-[#D92D20] px-2.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.25em]">
                    <FileWarning size={10} /> Mismatch
                  </button>
                  <button onClick={() => mark(r, "healthy")} data-testid={`source-mark-healthy-${hid}`}
                    className="inline-flex items-center gap-1 border border-[#5DD4B3]/40 hover:bg-[#0a1612] text-[#5DD4B3] px-2.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.25em]">
                    <CheckCircle2 size={10} /> Mark healthy
                  </button>
                  <button onClick={() => mark(r, "dead")} data-testid={`source-mark-dead-${hid}`}
                    className="inline-flex items-center gap-1 border border-[#454038] hover:border-[#D92D20] text-[#757065] hover:text-[#D92D20] px-2.5 py-1.5 font-mono text-[9px] uppercase tracking-[0.25em]">
                    <XCircle size={10} /> Mark dead
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editingRow && (
        <EditSourceModal
          row={editingRow}
          token={token} userToken={userToken}
          onClose={() => setEditingRow(null)}
          onSaved={() => { setEditingRow(null); load(); }}
        />
      )}
    </div>
  );
};

// =============================================================================
//   EditSourceModal — P1 Manual Edit Source / Replace URL
// =============================================================================
// Edits title / url / publisher / author / published_date / archived_url /
// notes on a single topic source. Preserves topic_id + source_idx so all
// existing references (relationships, evidence, audit) keep pointing at the
// same logical source. Confirms before URL swaps on public dossiers.
const EditSourceModal = ({ row, token, userToken, onClose, onSaved }) => {
  const hid = `${row.topic_id}:${row.source_idx}`;
  const [form, setForm] = useState({
    title:          row.title || "",
    url:            row.url || "",
    publisher:      row.publisher || "",
    author:         row.author || "",
    published_date: row.published_date || "",
    archived_url:   row.archived_url || "",
    notes:          row.manual_note || "",
  });
  const [keepStatus, setKeepStatus] = useState(false);
  const [archiveToWayback, setArchiveToWayback] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deps, setDeps] = useState(null);
  const [confirmStep, setConfirmStep] = useState(false);

  useEffect(() => {
    let alive = true;
    adminSourceDependencies(token, hid, userToken)
      .then((d) => { if (alive) setDeps(d); })
      .catch(() => { /* silent — modal still usable */ });
    return () => { alive = false; };
  }, [hid, token, userToken]);

  // Esc closes
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const urlChanged = form.url.trim() !== (row.url || "").trim();
  const urlValid   = /^https?:\/\/[^\s]+$/i.test(form.url.trim());
  const anyChange  = (
    form.title         !== (row.title || "")        ||
    form.url           !== (row.url || "")          ||
    form.publisher     !== (row.publisher || "")    ||
    form.author        !== (row.author || "")       ||
    form.published_date !== (row.published_date || "") ||
    form.archived_url  !== (row.archived_url || "") ||
    form.notes         !== (row.manual_note || "")
  );
  const backsPublic = deps?.backs_public_topic !== false;  // default to true for safety

  const doSave = async () => {
    if (!anyChange) { toast.error("No fields changed."); return; }
    if (urlChanged && !urlValid) {
      toast.error("URL must start with http:// or https://"); return;
    }
    // Build delta — only send fields the admin actually changed.
    const payload = {
      confirm_public_change: urlChanged && backsPublic,
      keep_status: keepStatus,
      archive_to_wayback: urlChanged && archiveToWayback,
    };
    const map = {
      title: "title", url: "url", publisher: "publisher", author: "author",
      published_date: "published_date", archived_url: "archived_url", notes: "notes",
    };
    for (const [k, src] of Object.entries(map)) {
      const before = src === "notes" ? (row.manual_note || "") : (row[src] || "");
      if ((form[k] || "") !== before) payload[k] = form[k] || null;
    }
    setSaving(true);
    try {
      const out = await adminSourceEdit(token, hid, payload, userToken);
      const bits = out.changed_fields.join(", ");
      toast.success(
        out.url_changed
          ? `Saved · URL replaced · ${out.health_status_reset ? "marked needs_review" : "status kept"}`
          : `Saved · updated ${bits}`,
      );
      // Non-blocking second toast for the Wayback archive outcome
      if (out.archive) {
        if (out.archive.ok) {
          toast.success("Saved to Wayback · archived_url populated", { duration: 6000 });
        } else {
          toast.warning(
            `Wayback save failed (${out.archive.error || "unknown"}) — URL change is saved`,
            { duration: 7000 },
          );
        }
      }
      onSaved && onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const onSavePressed = () => {
    if (urlChanged && backsPublic && !confirmStep) {
      setConfirmStep(true); return;
    }
    doSave();
  };

  const fieldCls = "w-full bg-[#0a0907] border border-[#2D2A26] focus:border-[#D92D20] outline-none px-2 py-1.5 font-mono text-[11px] text-[#E4DFD3] placeholder:text-[#757065]";

  return (
    <div
      data-testid="source-edit-modal"
      className="fixed inset-0 z-50 bg-black/70 backdrop-blur-[2px] flex items-start justify-center overflow-y-auto py-10 px-4"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="border border-[#D92D20]/40 bg-[#0F0F0F] w-full max-w-2xl">
        {/* Header */}
        <div className="px-4 py-3 border-b border-[#2D2A26] flex items-center justify-between">
          <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D92D20] flex items-center gap-2">
            <Pencil size={12} /> // EDIT SOURCE
          </div>
          <button onClick={onClose} data-testid="source-edit-close"
            className="text-[#A39E93] hover:text-[#D92D20]"><X size={16} /></button>
        </div>

        {/* Body */}
        <div className="p-4 space-y-3">
          <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065]">
            id: <span className="text-[#A39E93]">{hid}</span>
            {deps?.topic && (
              <> · dossier: <a href={`/topic/${deps.topic.slug}`} target="_blank" rel="noopener noreferrer"
                className="text-[#A39E93] hover:text-[#D92D20]">{deps.topic.title}</a></>
            )}
          </div>

          {backsPublic && (
            <div className="border border-[#E0A526]/40 bg-[#1a1408] px-3 py-2 flex items-start gap-2"
                 data-testid="source-edit-public-warning">
              <ShieldAlert size={13} className="text-[#E0A526] mt-0.5 shrink-0" />
              <div className="text-[11px] text-[#E4DFD3] leading-relaxed">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#E0A526]">PUBLIC DOSSIER · </span>
                This source backs content visible to the public. Changing the URL will reset the
                source-health status to <b>needs_review</b> and prompt a confirmation step.
              </div>
            </div>
          )}

          {deps?.other_topics_using_url?.length > 0 && (
            <div className="font-mono text-[10px] text-[#9FA8D4]">
              same URL also used by {deps.other_topics_using_url.length} other dossier(s) — edit applies only to this one
            </div>
          )}

          {/* Fields */}
          <Field label="title" data-testid-key="title">
            <input data-testid="source-edit-title" value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })} className={fieldCls} />
          </Field>

          <Field label="url">
            <input data-testid="source-edit-url" value={form.url}
              onChange={(e) => setForm({ ...form, url: e.target.value })} className={fieldCls} />
            {urlChanged && !urlValid && (
              <div className="font-mono text-[10px] text-[#D92D20] mt-1">Invalid URL format</div>
            )}
            {urlChanged && urlValid && (
              <div className="font-mono text-[10px] text-[#CBB892] mt-1">
                old: <span className="text-[#757065] break-all">{row.url}</span>
              </div>
            )}
          </Field>

          <div className="grid sm:grid-cols-2 gap-3">
            <Field label="publisher / outlet">
              <input data-testid="source-edit-publisher" value={form.publisher}
                onChange={(e) => setForm({ ...form, publisher: e.target.value })} className={fieldCls} />
            </Field>
            <Field label="author">
              <input data-testid="source-edit-author" value={form.author}
                onChange={(e) => setForm({ ...form, author: e.target.value })} className={fieldCls} />
            </Field>
            <Field label="published_date">
              <input data-testid="source-edit-published-date" value={form.published_date}
                onChange={(e) => setForm({ ...form, published_date: e.target.value })}
                placeholder="YYYY-MM-DD" className={fieldCls} />
            </Field>
            <Field label="archived_url">
              <input data-testid="source-edit-archived-url" value={form.archived_url}
                onChange={(e) => setForm({ ...form, archived_url: e.target.value })}
                placeholder="https://web.archive.org/..." className={fieldCls} />
            </Field>
          </div>

          <Field label="notes (admin)">
            <textarea data-testid="source-edit-notes" value={form.notes}
              onChange={(e) => setForm({ ...form, notes: e.target.value })}
              rows={3} className={fieldCls + " resize-y"} />
          </Field>

          {urlChanged && (
            <label className="flex items-start gap-2 font-mono text-[10px] text-[#A39E93]">
              <input type="checkbox" checked={keepStatus}
                data-testid="source-edit-keep-status"
                onChange={(e) => setKeepStatus(e.target.checked)} className="mt-0.5" />
              <span>Keep current source-health status (don&apos;t reset to needs_review).
                Use only if you&apos;ve already manually re-verified the new URL.</span>
            </label>
          )}

          {urlChanged && (
            <label className="flex items-start gap-2 font-mono text-[10px] text-[#A39E93]"
                   data-testid="source-edit-archive-toggle-label">
              <input type="checkbox" checked={archiveToWayback}
                data-testid="source-edit-archive-toggle"
                onChange={(e) => setArchiveToWayback(e.target.checked)} className="mt-0.5" />
              <span>
                Save this URL to <b className="text-[#5DD4B3]">Wayback Machine</b> after update,
                and auto-fill <code className="text-[#CBB892]">archived_url</code> on success.
                Wayback may take ~30 s. Failure won&apos;t roll back the URL change.
              </span>
            </label>
          )}

          {confirmStep && urlChanged && backsPublic && (
            <div className="border border-[#D92D20]/40 bg-[#1a0808] px-3 py-2 flex items-start gap-2"
                 data-testid="source-edit-confirm-step">
              <AlertTriangle size={13} className="text-[#D92D20] mt-0.5 shrink-0" />
              <div className="text-[11px] text-[#E4DFD3] leading-relaxed">
                <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-[#D92D20]">CONFIRM · </span>
                You are about to change the URL on a source attached to a <b>published</b>
                {" "}dossier{deps?.topic?.title ? ` ("${deps.topic.title}")` : ""}. Press <b>Save</b>
                {" "}again to apply.
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-4 py-3 border-t border-[#2D2A26] flex flex-wrap items-center justify-end gap-2">
          <button onClick={onClose} data-testid="source-edit-cancel"
            className="border border-[#454038] hover:border-[#D92D20] text-[#A39E93] px-3 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em]">
            Cancel
          </button>
          <button onClick={onSavePressed} disabled={saving || !anyChange || (urlChanged && !urlValid)}
            data-testid="source-edit-save"
            className="bg-[#D92D20] hover:bg-[#B92318] disabled:opacity-40 text-white px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] flex items-center gap-2">
            {saving ? <Loader2 size={11} className="animate-spin" /> : null}
            {urlChanged && backsPublic && !confirmStep ? "Review URL change…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
};

const Field = ({ label, children }) => (
  <div>
    <div className="font-mono text-[9px] uppercase tracking-[0.25em] text-[#757065] mb-1">{label}</div>
    {children}
  </div>
);

export default AdminSourcesPanel;
